import { useContext, useState } from 'react';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithCustomToken,
  Auth,
  // connectAuthEmulator
} from 'firebase/auth';
import appConfig from '@frontend/env';
import { sentry } from '@frontend/tracking';
import { FirestoreDbContext, FirestoreDbQueryResult } from './FirestoreContext';

const firebaseConfig = {
  apiKey: appConfig.FIREBASE_API_KEY,
  authDomain: appConfig.FIREBASE_AUTH_DOMAIN,
  projectId: appConfig.FIREBASE_PROJECT_ID,
  storageBucket: appConfig.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: appConfig.FIREBASE_MESSAGING_SENDER_ID,
  appId: appConfig.FIREBASE_APP_ID,
};

export const useFirestoreApp = () => {
  // using a ref to avoid re-initializing the app multiple times
  const [app] = useState(() => initializeApp(firebaseConfig));

  return app;
};

/**
 * This function serves as a wrapper for getAuth to ensure proper error handling
 * @param app
 * @returns auth
 */
const getFirestoreAuth = (app: FirebaseApp) => {
  let auth: Auth;
  try {
    auth = getAuth(app);
  } catch (e) {
    console.error('Could not get Firestore auth', e);
    return null;
  }
  // add this back when tests are ready
  // if (location.hostname === 'localhost') {
  //   connectAuthEmulator(auth, `http://127.0.0.1:9099`);
  // }
  return auth;
};

/**
 * @returns firestore db instance if sign-in's were successful, otherwise null
 */
export const useFirestoreDbQuery = (): FirestoreDbQueryResult => {
  const context = useContext(FirestoreDbContext);
  if (!context) {
    throw new Error('useFirestoreDbQuery must be used within a FirestoreDbProvider');
  }
  return context;
};

export const customSignIn = async (token: string, app: FirebaseApp) => {
  const auth = getFirestoreAuth(app);

  if (!auth) {
    throw new Error('No auth, unable to initialize firestore');
  }

  return signInWithCustomToken(auth, token)
    .then((user) => {
      return user;
    })
    .catch((err) => {
      sentry.warn({ error: err, topic: 'notifications', team: 'fe-ux-platform' });
      throw new Error('Could not validate firestore token');
    });
};
